import { useDebounceEffect } from 'ahooks';
import { useCallback, useRef, useState } from 'react';

import { ModuleSearchPostsReq, ModuleSupportCenterPost } from '@src/api/generated/data-contracts';
import { postSupportSearch } from '@src/api/generated/Support';

// 平台类型
export type PlatformCategory = 'all' | 'publisher' | 'advertiser';

// 单个平台的分页状态
export interface PlatformPaginationState {
  results: ModuleSupportCenterPost[];
  loading: boolean;
  loadingMore: boolean;
  totalCount: number;
  currentPage: number;
  hasMore: boolean;
}

// 所有平台的状态
export interface SupportSearchPaginationState {
  all: PlatformPaginationState;
  publisher: PlatformPaginationState;
  advertiser: PlatformPaginationState;
}

export interface UseSupportSearchPaginationReturn {
  state: SupportSearchPaginationState;
  loadMore: (platform: PlatformCategory) => Promise<void>;
  reset: () => void;
}

const PAGE_SIZE = 10;

// 初始化单个平台状态
const createInitialPlatformState = (): PlatformPaginationState => ({
  results: [],
  loading: false,
  loadingMore: false,
  totalCount: 0,
  currentPage: 1,
  hasMore: false,
});

// 初始化所有平台状态
const createInitialState = (): SupportSearchPaginationState => ({
  all: createInitialPlatformState(),
  publisher: createInitialPlatformState(),
  advertiser: createInitialPlatformState(),
});

/**
 * SupportSearch 页面搜索 hook
 * 按平台分别请求数据，支持分页加载，每次加载 10 个
 */
function useSupportSearchPagination(keyword: string): UseSupportSearchPaginationReturn {
  const [state, setState] = useState<SupportSearchPaginationState>(createInitialState);

  // 用于取消过期请求
  const searchVersionRef = useRef(0);

  // 搜索单个平台的第一页
  const searchPlatformFirstPage = useCallback(async (
    searchKeyword: string,
    platform: PlatformCategory,
    currentVersion: number,
  ) => {
    try {
      const searchParams: ModuleSearchPostsReq = {
        keyword: searchKeyword.trim(),
        page: 1,
        pageSize: PAGE_SIZE,
        withContent: true,
        searchType: 'both',
        // all 不传 platform 参数，表示搜索全部
        ...(platform !== 'all' && { platform }),
      };

      const response = await postSupportSearch(searchParams);

      // 检查是否为最新请求
      if (currentVersion !== searchVersionRef.current) {
        return;
      }

      if (response.code === 0 && response.data) {
        const items = response.data.items || [];
        const totalNumber = response.data.pageInfo?.totalNumber || 0;
        setState(prev => ({
          ...prev,
          [platform]: {
            results: items,
            loading: false,
            loadingMore: false,
            totalCount: totalNumber,
            currentPage: 1,
            hasMore: items.length < totalNumber,
          },
        }));
      } else {
        console.error(`Search ${platform} failed:`, response.msg);
        setState(prev => ({
          ...prev,
          [platform]: {
            ...createInitialPlatformState(),
            loading: false,
          },
        }));
      }
    } catch (error) {
      // 检查是否为最新请求
      if (currentVersion !== searchVersionRef.current) {
        return;
      }
      console.error(`Search ${platform} error:`, error);
      setState(prev => ({
        ...prev,
        [platform]: {
          ...createInitialPlatformState(),
          loading: false,
        },
      }));
    }
  }, []);

  // 首次搜索 - 同时请求三个平台的第一页
  const searchFirstPage = useCallback(async (searchKeyword: string) => {
    if (!searchKeyword.trim()) {
      setState(createInitialState());
      return;
    }

    // 增加版本号，用于取消过期请求
    searchVersionRef.current += 1;
    const currentVersion = searchVersionRef.current;

    // 设置所有平台为加载中
    setState({
      all: { ...createInitialPlatformState(), loading: true },
      publisher: { ...createInitialPlatformState(), loading: true },
      advertiser: { ...createInitialPlatformState(), loading: true },
    });

    // 并行请求三个平台的数据
    await Promise.all([
      searchPlatformFirstPage(searchKeyword, 'all', currentVersion),
      searchPlatformFirstPage(searchKeyword, 'publisher', currentVersion),
      searchPlatformFirstPage(searchKeyword, 'advertiser', currentVersion),
    ]);
  }, [searchPlatformFirstPage]);

  // 加载更多 - 针对特定平台
  const loadMore = useCallback(async (platform: PlatformCategory) => {
    const platformState = state[platform];

    if (platformState.loading || platformState.loadingMore || !platformState.hasMore || !keyword.trim()) {
      return;
    }

    const nextPage = platformState.currentPage + 1;

    // 设置该平台为加载更多中
    setState(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        loadingMore: true,
      },
    }));

    try {
      const searchParams: ModuleSearchPostsReq = {
        keyword: keyword.trim(),
        page: nextPage,
        pageSize: PAGE_SIZE,
        withContent: true,
        searchType: 'both',
        ...(platform !== 'all' && { platform }),
      };

      const response = await postSupportSearch(searchParams);

      if (response.code === 0 && response.data) {
        const newItems = response.data.items || [];
        const totalNumber = response.data.pageInfo?.totalNumber || 0;
        setState(prev => ({
          ...prev,
          [platform]: {
            ...prev[platform],
            results: [...prev[platform].results, ...newItems],
            loadingMore: false,
            currentPage: nextPage,
            totalCount: totalNumber,
            hasMore: prev[platform].results.length + newItems.length < totalNumber,
          },
        }));
      } else {
        console.error(`Load more ${platform} failed:`, response.msg);
        setState(prev => ({
          ...prev,
          [platform]: {
            ...prev[platform],
            loadingMore: false,
          },
        }));
      }
    } catch (error) {
      console.error(`Load more ${platform} error:`, error);
      setState(prev => ({
        ...prev,
        [platform]: {
          ...prev[platform],
          loadingMore: false,
        },
      }));
    }
  }, [keyword, state]);

  // 重置搜索状态
  const reset = useCallback(() => {
    searchVersionRef.current += 1;
    setState(createInitialState());
  }, []);

  // 防抖搜索 - keyword 变化时自动搜索第一页
  useDebounceEffect(
    () => {
      if (keyword) {
        searchFirstPage(keyword);
      } else {
        reset();
      }
    },
    [keyword],
    { wait: 300 },
  );

  return {
    state,
    loadMore,
    reset,
  };
}

export default useSupportSearchPagination;
